import React, { useState } from "react";
import * as XLSX from "xlsx";

const pad = (n: string) => n.padStart(2, "0");

function parseDate(v: any): string {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "number") {
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    return d.toISOString().slice(0, 10);
  }
  const s = String(v).trim();
  let m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (m) return `${m[3]}-${pad(m[2])}-${pad(m[1])}`; // UK dd/mm/yyyy
  m = s.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

function money(v: any): number {
  if (v === undefined || v === null) return 0;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return isNaN(n) ? 0 : n;
}

function parseExtras(v: any): { desc: string; amount: number }[] {
  if (!v) return [];
  return String(v).split(/[;|]/).map(part => {
    const [desc, amt] = part.split(":");
    return { desc: (desc || "").trim(), amount: money(amt) };
  }).filter(e => e.desc);
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const HEADER_MAP: Record<string, string[]> = {
  name: ["name", "fullname", "resident", "residentname", "serviceuser"],
  dob: ["dob", "dateofbirth", "birth"],
  admissionDate: ["admissiondate", "admitted", "admission", "startdate", "movein", "moveindate", "placedate"],
  exitDate: ["leavingdate", "leftdate", "exit", "departure", "exitdate"],
  room: ["room", "bed", "roomno"],
  nhsNumber: ["nhsnumber", "nhsno"],
  fundingType: ["fundingtype", "funding", "payer", "funder"],
  council: ["council", "localauthority", "la", "icb", "nhsicb"],
  totalFee: ["totalmonthlyfee", "totalfee", "monthlyfee", "fee", "weeklyfee"],
  councilPays: ["councilshare", "councilnsshare", "lashare", "nsshare", "councilpays"],
  residentPays: ["customershare", "residentshare", "customerresidentshare", "residentpays", "privateshare"],
  familyName: ["familycontact", "nextofkin", "contact", "family"],
  familyPhone: ["familyphone", "contactphone"],
  familyEmail: ["familyemail", "contactemail"],
  relation: ["relationship", "relation"],
  legalRep: ["legalrepresentative", "legalrep", "solicitor"],
  gpName: ["gpname", "gp"],
  gpPhone: ["gpphone"],
  dietary: ["dietary", "diet"],
  allergies: ["allergies", "allergy"],
  mobility: ["mobility"],
  extras: ["extras", "additional", "addons"],
  dols: ["dols", "deprivationofliberty", "mca"],
};

const get = (key: string): any[] => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } };
const setKey = (key: string, d: any[]) => localStorage.setItem(key, JSON.stringify(d));

export default function BulkUpload({ user }: { user?: any }) {
  const [rows, setRows] = useState<any[]>([]);
  const [message, setMessage] = useState("");
  const [fileName, setFileName] = useState("");

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });
        const parsed = json.map(r => {
          const rec: any = {};
          Object.keys(HEADER_MAP).forEach(key => {
            const synonyms = HEADER_MAP[key];
            const found = Object.keys(r).find(h => synonyms.includes(norm(h)));
            rec[key] = found !== undefined ? r[found] : "";
          });
          return rec;
        }).filter(r => r.name);

        const shaped = parsed.map((r, i) => {
          const total = money(r.totalFee);
          const council = money(r.councilPays);
          const resident = money(r.residentPays);
          let funding = String(r.fundingType || "").trim();
          let councilShare = council;
          let residentShare = resident;
          // Auto-work-out funding + split
          if (!funding) funding = r.council ? "Council" : /nhs|icb/i.test(String(r.council)) ? "NHS" : total && !council && !resident ? "Self-funding" : "Council";
          if (/self|private/i.test(funding)) { residentShare = residentShare || total; councilShare = 0; }
          else if (/nhs|icb/i.test(funding)) { councilShare = councilShare || total; residentShare = residentShare || 0; }
          else { if (total && (councilShare || residentShare)) { if (!councilShare) councilShare = total - residentShare; if (!residentShare) residentShare = total - councilShare; } else if (total) { councilShare = councilShare || total; } }
          return {
            id: `imp-${Date.now()}-${i}`,
            name: String(r.name).trim(),
            dob: parseDate(r.dob),
            admissionDate: parseDate(r.admissionDate),
            exitDate: parseDate(r.exitDate),
            room: String(r.room || ""),
            nhsNumber: String(r.nhsNumber || ""),
            fundingType: funding,
            council: String(r.council || ""),
            totalFee: String(total || (councilShare + residentShare)),
            councilPays: String(councilShare),
            residentPays: String(residentShare),
            familyName: String(r.familyName || ""),
            familyPhone: String(r.familyPhone || ""),
            familyEmail: String(r.familyEmail || ""),
            relation: String(r.relation || ""),
            legalRep: String(r.legalRep || ""),
            gpName: String(r.gpName || ""),
            gpPhone: String(r.gpPhone || ""),
            dietary: String(r.dietary || ""),
            allergies: String(r.allergies || ""),
            mobility: String(r.mobility || ""),
            extras: parseExtras(r.extras),
            dols: /yes|y|true|1/i.test(String(r.dols)),
            notes: "Imported from Excel",
          };
        });
        setRows(shaped);
        setMessage(`Read ${shaped.length} resident(s) from ${file.name}. Check the preview, then click Import.`);
      } catch {
        setMessage("Could not read that file. Save it as .xlsx or .csv and try again.");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const doImport = () => {
    const residents = get("residents_records");
    const mca = get("mca_hub_records");
    const existing = new Set(residents.map((r: any) => r.name));
    let added = 0;
    rows.forEach(r => {
      if (existing.has(r.name)) return;
      const { dols, ...rest } = r;
      residents.unshift(rest);
      existing.add(r.name);
      added++;
      if (dols) mca.unshift({ id: `mca-imp-${Date.now()}-${r.id}`, kind: "dols", residentName: r.name, date: r.admissionDate, decision: "", outcome: "", assessor: "", evidence: "", attendees: "", decisionMade: "", rationale: "", applicationDate: "", authorisation: "", expiryDate: "", conditions: "Imported - review required", status: "Authorised" });
    });
    setKey("residents_records", residents);
    setKey("mca_hub_records", mca);
    setMessage(`Imported ${added} resident(s). Open Residents & Invoicing and click your normal invoice generation to bill them all.`);
    setRows([]);
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Bulk Upload & Auto-Billing</h2>
        <p className="text-sm text-slate-600">Drop in your existing Excel/CSV of residents. The system reads UK dates, works out funding and the council/customer split, reads extras and DoLS, and imports everyone in one click.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <label className="block text-sm font-medium text-slate-700">Upload spreadsheet (.xlsx or .csv){fileName ? ` - ${fileName}` : ""}</label>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="mt-2 block w-full text-sm" />
        <p className="mt-2 text-xs text-slate-500">Any column headings work (Name, Full Name, Resident...; Admission Date, Admitted, Start Date...; dates as dd/mm/yyyy). Extras in one cell like: Haircut:12.50;Drinks:8.00</p>
      </div>

      {rows.length ? (
        <div className="space-y-3">
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr><th className="p-2">Name</th><th className="p-2">Admitted</th><th className="p-2">Room</th><th className="p-2">Funding (auto)</th><th className="p-2">Council</th><th className="p-2">Council share</th><th className="p-2">Customer share</th><th className="p-2">Extras</th><th className="p-2">DoLS</th></tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="p-2 font-medium">{r.name}</td>
                    <td className="p-2">{r.admissionDate}</td>
                    <td className="p-2">{r.room}</td>
                    <td className="p-2">{r.fundingType}</td>
                    <td className="p-2">{r.council}</td>
                    <td className="p-2">£{r.councilPays}</td>
                    <td className="p-2">£{r.residentPays}</td>
                    <td className="p-2">{r.extras.length ? r.extras.map((e: any) => `${e.desc} £${e.amount}`).join(", ") : "-"}</td>
                    <td className="p-2">{r.dols ? "Yes" : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={doImport} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Import {rows.length} resident(s)</button>
        </div>
      ) : null}
    </div>
  );
}